import { AxiosResponse } from 'axios';
import React from 'react';
import { SidebarAd } from 'googlead/components/SidebarAd';
import {
  fetchResortAccommodations,
  fetchResortDailyLiftTickets,
  fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortSeasonLiftTickets,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortSnowReport,
} from 'api/resort/static';
import {
  IDailyResortLiftTickets,
  IRelatedNews, IResortPageInfo, IResortPageSnowInfo, IResortPathInfo, ISeasonResortLiftTickets,
} from 'api/resort/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { restructureDailyGridData } from 'components/resort/ResortLiftTicketsGrid/restructureDailyGridData';
import { generateSeasonsGridColumns } from 'components/resort/ResortLiftTicketsGrid/gridColumnsSeasons';
import { generateDailyGridColumns } from 'components/resort/ResortLiftTicketsGrid/gridColumnsDaily';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { SkipassesLiftTicketsInfo } from 'components/region/SkipassesLiftTicketsInfo';
import ResortLiftTicketsGrid from 'components/resort/ResortLiftTicketsGrid/Grid';
import { ticketsTypes } from 'components/resort/ResortLiftTicketsGrid/constants';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SpecialNote from 'components/resort/SpecialNoteBlock';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Side } from 'components/resort/Layout/Side';
import { Main } from 'components/resort/Layout/Main';
import { IResortLayout } from 'page-components/resort/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { formatDateFromStr } from 'util/formatDate';
import { getResortSlug } from 'util/memcache';

import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import styles from './lift-tickets.module.scss';

export type IResortLiftTicketsProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  seasonLiftTickets: ISeasonResortLiftTickets
  dailyLiftTickets: IDailyResortLiftTickets
  relatedNews: IRelatedNews | null,
  accommodations: IAccommodationPage | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-lift-tickets-page';
export const TEMPLATE_ID = '3.2.6';
export const TEMPLATE_NAME = 'Resort Lift Tickets';

export default function ResortLiftTickets({
  seasonLiftTickets,
  dailyLiftTickets,
  relatedNews,
  pathInfo,
  fullResort,
}: IResortLiftTicketsProps) {
  const { t } = useOTSTranslation('common');

  const seasonLiftTicketsColumns = generateSeasonsGridColumns(seasonLiftTickets);
  const dailyLiftTicketsColumns = generateDailyGridColumns(dailyLiftTickets);
  const dailyLiftTicketsData = restructureDailyGridData(dailyLiftTickets);
  const showSeasonPasses = ticketsTypes.some((i) => seasonLiftTickets[`${i}Price` as keyof ISeasonResortLiftTickets]);

  const updatedTimeDailyPasses = dailyLiftTickets.lastModified
    ? formatDateFromStr(dailyLiftTickets.lastModified)
    : '';
  const updatedTimeSeasonPasses = seasonLiftTickets.lastModified
    ? formatDateFromStr(seasonLiftTickets.lastModified)
    : '';

  const showSeasonBlock = !!showSeasonPasses || !!seasonLiftTickets.note || !!seasonLiftTickets.purchaseUrl;
  const showDailyBlock = !!dailyLiftTicketsData || !!dailyLiftTickets.note || !!dailyLiftTickets.purchaseUrl;

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.liftTickets.seoTitle', { resort: pathInfo.title })} />
          <BlockContent>
            {t('resort.liftTickets.seoText', { resort: pathInfo.title })}
          </BlockContent>
        </BoxWithShadow>
        {showSeasonBlock && (
          <BoxWithShadow>
            <BlockHeader title={t('resort.liftTickets.seasonPasses', { resort: pathInfo.title })} />
            <BlockContent className={styles.blockContentWrapper}>
              {showSeasonPasses && (
                <ResortLiftTicketsGrid
                  columns={seasonLiftTicketsColumns}
                  data={[seasonLiftTickets]}
                  currency={seasonLiftTickets.currency}
                />
              )}
              <SpecialNote
                note={seasonLiftTickets.note}
                purchaseUrl={seasonLiftTickets.purchaseUrl}
                disclaimer={t('resort.seasonPass.disclaimer', { passlastupdated: updatedTimeSeasonPasses })}
                isPartner={fullResort.isPartner}
              />
            </BlockContent>
          </BoxWithShadow>
        )}
        {showDailyBlock && (
          <BoxWithShadow>
            <BlockHeader title={t('resort.liftTickets.dailyTickets', { resort: pathInfo.title })} />
            <BlockContent className={styles.blockContentWrapper}>
              {dailyLiftTicketsData && (
                <ResortLiftTicketsGrid
                  columns={dailyLiftTicketsColumns}
                  data={dailyLiftTicketsData}
                  currency={dailyLiftTickets.currency}
                />
              )}
              <SpecialNote
                note={dailyLiftTickets.note}
                purchaseUrl={dailyLiftTickets.purchaseUrl}
                disclaimer={t('resort.seasonPass.disclaimer', { passlastupdated: updatedTimeDailyPasses })}
                isPartner={fullResort.isPartner}
              />
            </BlockContent>
          </BoxWithShadow>
        )}
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={pathInfo.title}
          margin={{ marginTop: '-1.5rem', marginBottom: '2rem' }}
        />
        <PlanYourTrip
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          resort={pathInfo}
          liftsUrl={fullResort.liftsUrl}
          rentalUrl={fullResort.rentalUrl}
          lessonsUrl={fullResort.lessonsUrl}
          isPartner={fullResort.isPartner}
          resortCountry={fullResort?.region?.country_code}
        />
        <SkipassesLiftTicketsInfo
          header={t('resort.liftTickets.passSeoInfoTitle', { resort: pathInfo.title })}
          seoText={t('resort.liftTickets.passSeoInfoText', {
            resort: pathInfo.title,
            opendate: formatDateFromStr(fullResort.status.openingDate),
            closedate: formatDateFromStr(fullResort.status.closingDate),
            runs: fullResort.runs?.total,
            lifts: fullResort.lifts?.total,
            phone: fullResort.phone,
          })}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<IResortLiftTicketsProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const dailyLiftTickets = await fetchResortDailyLiftTickets(uuid);
  const seasonLiftTickets = await fetchResortSeasonLiftTickets(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid);

  return {
    type: 'resort',
    route: 'lift-tickets',
    pathInfo: cache.data,
    dailyLiftTickets,
    seasonLiftTickets,
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    relatedNews,
    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.LIFT_TICKETS,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort lift-tickets',
    hero: {
      title: 'resort.liftTickets.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.lifttickets.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.lifttickets.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortLiftTicketsStaticProps = wrapStaticProps(fetchProps);

export const getResortLiftTicketsServerSideProps = wrapServerSideProps(fetchProps);
